import React, { useEffect, useReducer, useState, useRef } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { withApollo, compose } from 'react-apollo';
import lodashSet from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import Header from 'chameleon/ui-stack/ui/page/header';
import log from 'chameleon/ui-stack/utilities/log';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import { getSubjectSpecificBucketsQuery } from './utility/query';
import DefaultDynamicBuilder from './DefaultDynamicBuilder';
import SpecificDynamicBuilder from './SpecificDynamicBuilder';
import SpecificBuilderDetails from './SpecificBuilderDetails';
import DynamicBuilderForm from './DynamicBuilderForm';
import { initialPageState, pageReducer, types } from './state';
import { getActiveQuestionTypes } from './utility/utils';
import DynamicBuilderContext from './context';
import create from './utility/create';

const DynamicAssemessmentsPage = (props) => {
  const { client, subjectName, masterSubjectId, router } = props;
  const [specificBuilderFeedback, setSpecificBuilderFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, dispatch] = useReducer(pageReducer, initialPageState);
  const { number, mcqLength, frqLength, buckets } = formData;
  const defaultBuckets = useRef([]);
  const isDefaultBuilder = buckets.length === 0;

  useEffect(() => {
    if (masterSubjectId) {
      let payload = {
        placeholderTitle: `${subjectName} - ${new Date().toLocaleString()}`,
        masterSubjectId,
        buckets: [],
      };

      (async () => {
        let result;

        try {
          result = await client.query({
            query: getSubjectSpecificBucketsQuery,
            variables: {
              masterSubjectId,
            },
          });

          if (result && result.data) {
            const { dynamicQuizBuckets } = result.data;

            if (dynamicQuizBuckets) {
              defaultBuckets.current = dynamicQuizBuckets.map(
                (tagCategoryData) => ({
                  label: tagCategoryData.name,
                  buckets: tagCategoryData.buckets.map((bucketData) => ({
                    label: bucketData.name,
                    mcq: bucketData.mcq,
                    frq: bucketData.frq,
                  })),
                }),
              );
              payload.buckets = cloneDeep(defaultBuckets.current);
            }

            setIsLoading(false);

            dispatch({
              type: types.UpdateFormData,
              payload,
            });
          }
        } catch (e) {
          log.error(e);
        }
      })();
    }
  }, [masterSubjectId]);

  const onInputChange =
    (field, objectToUpdate = null, propertyToUpdate = null) =>
    (e) => {
      const { value } = e.target || {};
      let data = {};
      switch (field) {
        case 'mcqLength':
        case 'frqLength': {
          data[field] = value !== '' ? parseInt(value, 10) : '';

          const timestamp = new Date().toLocaleString();
          const questionType = getActiveQuestionTypes({
            ...formData,
            ...data,
          });
          data.placeholderTitle = `${subjectName} - ${questionType} ${timestamp}`;
          break;
        }
        case 'excludeInterval':
        case 'number':
          data[field] = value !== '' ? parseInt(value, 10) : '';
          break;
        case 'buckets': {
          const newObject = cloneDeep(objectToUpdate);
          lodashSet(
            newObject,
            propertyToUpdate.split('.'),
            value !== '' ? parseInt(value, 10) : '',
          );

          const newData = formData[field].map((tagCategoryData) => {
            const objectIndex = tagCategoryData.buckets.indexOf(objectToUpdate);

            if (objectIndex < 0) {
              return tagCategoryData;
            }

            return {
              ...tagCategoryData,
              buckets: [
                ...tagCategoryData.buckets.slice(0, objectIndex),
                newObject,
                ...tagCategoryData.buckets.slice(objectIndex + 1),
              ],
            };
          });

          data = { [field]: newData };
          break;
        }
        default:
          data = { [field]: value };
          break;
      }
      dispatch({
        type: types.UpdateFormData,
        payload: data,
      });
    };

  let content;
  if (isDefaultBuilder) {
    content = <DefaultDynamicBuilder />;
  } else {
    content = <SpecificDynamicBuilder />;
  }

  const hasQuestions = mcqLength > 0 || frqLength > 0;
  const disableSubmit =
    isSubmitting || (isDefaultBuilder && !hasQuestions) || number < 1;

  const onSubmit = async () => {
    setIsSubmitting(true);
    const redirect = isDefaultBuilder;
    await create(client, router, formData, redirect, (data) => {
      if (!isDefaultBuilder) {
        setSpecificBuilderFeedback(data);
      }

      dispatch({
        type: types.ResetFormData,
      });
    });
    setIsSubmitting(false);
  };

  const onFormReset = () => {
    setSpecificBuilderFeedback(null);
    dispatch({
      type: types.UpdateFormData,
      payload: {
        buckets: defaultBuckets.current,
      },
    });
  };

  return (
    <DynamicBuilderContext.Provider
      value={{ formData, onInputChange, dispatch }}
    >
      <Header title="Dynamic Assessments Builder" />
      {isLoading ? (
        <LoadingSpinner />
      ) : specificBuilderFeedback ? (
        <SpecificBuilderDetails
          {...specificBuilderFeedback}
          onAcknowledge={onFormReset}
          masterSubjectId={masterSubjectId}
        />
      ) : (
        <DynamicBuilderForm disableSubmit={disableSubmit} onSubmit={onSubmit}>
          {content}
        </DynamicBuilderForm>
      )}
    </DynamicBuilderContext.Provider>
  );
};

const mapStateToProps = ({ userData, subject: { data: subjectData } }) => ({
  userData,
  subjectName: subjectData?.name,
  masterSubjectId: subjectData ? Number.parseInt(subjectData.id, 10) : null,
});

export default compose(
  withRouter,
  withApollo,
  connect(mapStateToProps),
)(DynamicAssemessmentsPage);
